"use client"

import { create } from 'zustand'
import {
  applyLayoutPreferences,
  isRailWidth,
  LAYOUT_DEFAULTS,
  LAYOUT_STORAGE_KEY,
  parseLayoutPreferences,
  resolveSidebarWidth,
  SIDEBAR_RAIL_WIDTH,
  type Density,
  type LayoutPreferences,
} from '@/lib/layout-preferences'

/**
 * Density and sidebar geometry, held once and written straight to `<html>`.
 *
 * The inline script in `index.html` has already applied the stored values
 * before React mounts — this store exists so the drag handle and the settings
 * UI can change them, not to own the initial paint.
 */
interface LayoutState extends LayoutPreferences {
  setDensity: (density: Density) => void
  /** During a drag: applies immediately, does not touch storage. */
  previewSidebarWidth: (width: number) => void
  /** On release: snaps, clamps and persists. */
  commitSidebarWidth: (width: number) => void
  /** Rail ⇄ the width it was last expanded to. */
  toggleSidebar: () => void
}

function initial(): LayoutPreferences {
  if (typeof window === 'undefined') return { ...LAYOUT_DEFAULTS }
  return parseLayoutPreferences(window.localStorage.getItem(LAYOUT_STORAGE_KEY))
}

function persist(preferences: LayoutPreferences) {
  applyLayoutPreferences(preferences)
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(preferences))
  } catch {
    // Private browsing: the change still applies for this session.
  }
}

export const useLayoutStore = create<LayoutState>()((set, get) => ({
  ...initial(),

  setDensity: (density) => {
    const next = { ...get(), density }
    persist(next)
    set({ density })
  },

  // Written to the element but not to storage: a drag fires this on every
  // pointer move, and localStorage is not a per-frame API.
  //
  // The width is snapped live rather than on release, so the rail appears
  // under the cursor instead of jumping into place afterwards. React state is
  // touched only when the drag actually crosses the snap point — that is what
  // swaps labels for icons, and it happens once or twice per gesture, not
  // sixty times a second.
  previewSidebarWidth: (width) => {
    const resolved = resolveSidebarWidth(width)
    document.documentElement.style.setProperty('--sidebar-w', `${resolved}px`)

    if (isRailWidth(resolved) !== isRailWidth(get().sidebarWidth)) {
      set({ sidebarWidth: resolved })
    }
  },

  commitSidebarWidth: (width) => {
    const sidebarWidth = resolveSidebarWidth(width)
    // Dragging out to a real width also updates what a later collapse will
    // restore to; dragging *into* the rail must not, or expanding would return
    // to the rail it just left.
    const sidebarRestoreWidth = isRailWidth(sidebarWidth)
      ? get().sidebarRestoreWidth
      : sidebarWidth

    const next = { ...get(), sidebarWidth, sidebarRestoreWidth }
    persist(next)
    set({ sidebarWidth, sidebarRestoreWidth })
  },

  toggleSidebar: () => {
    const { sidebarWidth, sidebarRestoreWidth } = get()
    const collapsing = !isRailWidth(sidebarWidth)

    const next = {
      ...get(),
      sidebarWidth: collapsing ? SIDEBAR_RAIL_WIDTH : sidebarRestoreWidth,
      sidebarRestoreWidth: collapsing ? sidebarWidth : sidebarRestoreWidth,
    }
    persist(next)
    set({ sidebarWidth: next.sidebarWidth, sidebarRestoreWidth: next.sidebarRestoreWidth })
  },
}))
